import {AccessTime} from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import format from "date-fns/format";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ShiftBySkillsTag } from "../../../../export/shift-scheduler";
import { JOB, SKILLS } from "../../../assets/constants";
import {
  formatDurationAbbreviated,
  getConfirmedWorkersCount,
  getTotalWorkersCount,
  isTooDark,
} from "../../../utils/helpers";
import { StoreType } from "../../../utils/types";
import {minTimeSlotHeight} from "./constants";

type BoxRowProps = {
  icon: React.ReactNode;
  content: string;
  fontWeight?: number;
  fontColor: string;
  fontSize: number;
};
const BoxRow = ({
  icon,
  content,
  fontWeight,
  fontColor,
  fontSize,
}: BoxRowProps) => {
  return (
    <Box display="flex" alignItems="flex-start" pb={1}>
      {icon}
      <Box ml={1}>
        <Typography
          flexWrap={"wrap"}
          sx={{
            color: fontColor,
            whiteSpace: "normal",
            fontFamily: "Roboto",
            fontSize: `${fontSize}px`,
            lineHeight: `${fontSize + 8}px`,
            fontWeight: `${fontWeight || 400}`,
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

export const Shift = ({ data, isLayer = false, infoAtBottom = false, timeSlotHeight = minTimeSlotHeight, offsetHours = 0}: any): JSX.Element => {
  const { t } = useTranslation();
  const shiftByType = data.jobId === 0 ? SKILLS : JOB;

  const textColor = useSelector((state: any) => {
    if (shiftByType === SKILLS) {
      // Shift by Skills
      return "rgba(0, 0, 0, 0.87)";
    } else {
      // Shift by Job
      const jobRole =
        state.supervisor?.filterData?.allJobRoles.length > 0 &&
        state.supervisor?.filterData?.allJobRoles?.filter(
          (jobRole: { id: string | number }) => String(jobRole.id) === String(data.jobId)
        );
      return isTooDark(`#${jobRole[0]?.hexColor}`) ? "#FFFFFF" : "#030303";
    }
  });

  const shiftJob = useSelector((state: any) => {
    if (shiftByType === JOB) {
      // Shift by Job
      const jobRole =
        state.supervisor?.filterData?.allJobRoles.length > 0 &&
        state.supervisor?.filterData?.allJobRoles?.find(
          (jobRole: { id: string | number }) => String(jobRole.id) === String(data.jobId)
        );
      if (jobRole) return jobRole;
      else return { name: "-", maxWorkers: 0 };
    } else {
      return { name: "-", maxWorkers: 0 };
    }
  });

  const shiftSkills = useSelector((state: any) => {
    if (shiftByType === SKILLS) {
      return data.skills ?? [];
    } else {
      return [];
    }
  });

  const urgentShiftEnabledLocations = useSelector(
    (state: StoreType) => state.user.userData.urgentShiftEnabledLocations
  );

  const locationId = useSelector((state: StoreType) => {
    const workcenter = state.supervisor?.filterData?.allWorkcenters?.find(
      (workcenter: any) => workcenter.id === data.workCenterId
    );
    if (
      workcenter &&
      Object.prototype.hasOwnProperty.call(workcenter, "locationId")
    ) {
      return workcenter.locationId;
    } else {
      return "";
    }
  });

  const isUrgentEnabled = data.isUrgent && urgentShiftEnabledLocations?.includes(locationId);

  const locationName = useSelector((state: any) => {
    const workcenter = state.supervisor?.filterData?.allWorkcenters?.find(
      (workcenter: any) => workcenter.id === data.workCenterId
    );
    if (workcenter && Object.prototype.hasOwnProperty.call(workcenter, "locationId")) {
      const location = state.supervisor?.filterData?.allLocations?.find(
        (location: any) => location.id === workcenter.locationId
      );
      if (location) {
        return location.name;
      } else return null;
    }
  });

  const effectingShiftEndHourIndex = data.shiftEndHourIndex - offsetHours;
  const paddingBottom = effectingShiftEndHourIndex*timeSlotHeight - timeSlotHeight*(data.shiftBottom/100) - 3;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent={infoAtBottom ? "flex-end" : "flex-start"}
      width={1}
      sx={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        padding: "6px 2px",
        paddingBottom: infoAtBottom
          ? `${data.shiftEndHourIndex > 0 ? paddingBottom : 6}px`
          : "6px",
      }}
    >
      <></>
      {!data.isPublished ? (
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" gap={0.5} flexWrap="wrap">
            <Box
              mb={1}
              sx={{
                padding: "1px 7px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                border: 1,
                borderWidth: "1px",
                borderColor: "rgba(0,0,0,0.12)",
                borderRadius: "4px",
                backgroundColor: "#FFF",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  color: "rgba(0,0,0,0.6)",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  letterSpacing: "0.4px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("flex-scheduler_draft")}
              </Typography>
              <EditOutlinedIcon
                sx={{
                  color: "#424242",
                  fontSize: "16px",
                  marginLeft: "4px",
                }}
              />
            </Box>
            {isUrgentEnabled && (
              <Box
                mb={1}
                sx={{
                  padding: "1px 7px",
                  display: "inline-flex",
                  flexDirection: "row",
                  alignItems: "center",
                  border: 1,
                  borderWidth: "1px",
                  borderColor: "#D32F2F",
                  borderRadius: "4px",
                  backgroundColor: "#FFEBEE",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    color: "#D32F2F",
                    fontSize: "12px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    letterSpacing: "0.4px",
                  }}
                >
                  {t("flex-scheduler_urgent")}
                </Typography>
                <ErrorOutlineIcon
                  sx={{
                    color: "#D32F2F",
                    fontSize: "16px",
                    marginLeft: "4px",
                  }}
                />
              </Box>
            )}
          </Box>
          {shiftByType === JOB && (
            <BoxRow
              content={shiftJob.name ?? t("flex-scheduler_job-role")}
              icon={
                <WorkHistoryOutlinedIcon
                  style={{
                    color: "rgba(0,0,0,0.87)",
                    fontSize: "16px",
                    marginTop: "2px",
                  }}
                />
              }
              fontWeight={500}
              fontSize={14}
              fontColor={"rgba(0,0,0,0.87)"}
            />
          )}
          {shiftByType === SKILLS && (
            <ShiftBySkillsTag
              skills={shiftSkills}
              fontSize={12}
              iconSize={16}
              iconColor={"rgba(0,0,0,0.87)"}
            />
          )}
          <BoxRow
            content={locationName ?? t("flex-scheduler_location")}
            icon={
              <LocationOnIcon
                style={{
                  color: "rgba(0,0,0,0.87)",
                  fontSize: "16px",
                  marginTop: "2px",
                }}
              />
            }
            fontWeight={400}
            fontSize={12}
            fontColor={textColor}
          />
          {isLayer && (
            <BoxRow
              content={`${format(
                new Date(data.startDateTime),
                "h:mm a"
              )} - ${format(new Date(data.endDateTime), "h:mm a")}`}
              icon={
                <AccessTime
                  style={{
                    color: shiftByType === SKILLS ? "#757575" : textColor,
                    fontSize: "16px",
                    marginTop: "2px",
                  }}
                />
              }
              fontWeight={400}
              fontSize={12}
              fontColor={textColor}
            />
          )}
        </Box>
      ) : (
        <Tooltip
          title={
            data.hasWorkersFromOtherLocations
              ? t("flex-scheduler_workers-multiple-locations-bid")
              : null
          }
          disableInteractive
          placement="top"
        >
          <Box
            display="flex"
            flexDirection="column"
            width={"100%"}
            flex={infoAtBottom ? "unset" : 1}
            justifyContent={infoAtBottom ? "flex-end" : "space-between"}
          >
            {isUrgentEnabled && (
              <Box mb={1} display="flex" flexDirection="row">
                <Box
                  sx={{
                    padding: "1px 7px",
                    display: "inline-flex",
                    flexDirection: "row",
                    alignItems: "center",
                    border: 1,
                    borderWidth: "1px",
                    borderColor: "#D32F2F",
                    borderRadius: "4px",
                    backgroundColor: "#FFEBEE",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      color: "#D32F2F",
                      fontSize: "12px",
                      fontWeight: "500",
                      lineHeight: "20px",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {t("flex-scheduler_urgent")}
                  </Typography>
                  <ErrorOutlineIcon
                    sx={{
                      color: "#D32F2F",
                      fontSize: "16px",
                      marginLeft: "4px",
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                width: "100%",
              }}
            >
              {shiftByType === JOB && (
                <BoxRow
                  content={shiftJob.name ?? t("flex-scheduler_job-role")}
                  icon={
                    <WorkHistoryOutlinedIcon
                      style={{
                        color: textColor,
                        fontSize: "16px",
                        marginTop: "2px",
                      }}
                    />
                  }
                  fontWeight={500}
                  fontSize={14}
                  fontColor={textColor}
                />
              )}
              {shiftByType === SKILLS && (
                <ShiftBySkillsTag
                  skills={shiftSkills}
                  iconSize={16}
                  fontSize={12}
                  iconColor={"#757575"}
                />
              )}
              <BoxRow
                content={(() => {
                  const needed = parseInt(data?.numberOfWorkersNeeded) || 0;
                  const isFlexi = data?.isFlexiSignupEnabled;
                  const isPartial = data?.isPartialSignupEnabled;

                  if (isFlexi || isPartial) {
                    const shiftStart = new Date(data?.startDate || data?.startDateTime);
                    const shiftEnd = new Date(data?.endDate || data?.endDateTime);
                    const shiftDurationHours = (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
                    const totalHoursRequiredRaw = needed * shiftDurationHours;
                    const coveredHoursRaw = (data?.slotsFilled || 0) * shiftDurationHours;

                    if (coveredHoursRaw === 0) {
                      return t("flex-scheduler_open-duration", {
                        duration: formatDurationAbbreviated(totalHoursRequiredRaw),
                      });
                    } else {
                      const percentage = needed > 0 ? Math.round(((data?.slotsFilled || 0) / needed) * 100) : 0;
                      return `${percentage}% • ${formatDurationAbbreviated(coveredHoursRaw)}/${formatDurationAbbreviated(totalHoursRequiredRaw)}`;
                    }
                  } else {
                    const signedUp = getConfirmedWorkersCount(data);
                    if (signedUp === 0) {
                      return t("flex-scheduler_open-workers", { count: needed });
                    } else {
                      return t("flex-scheduler_signed-up-workers", { signedUp, needed });
                    }
                  }
                })()}
                icon={
                  <ThumbUpOutlinedIcon
                    style={{
                      color: shiftByType === SKILLS ? "#757575" : textColor,
                      fontSize: "16px",
                      marginTop: "2px",
                    }}
                  />
                }
                fontWeight={400}
                fontSize={12}
                fontColor={textColor}
              />
              <BoxRow
                content={locationName ?? t("flex-scheduler_location")}
                icon={
                  <LocationOnIcon
                    style={{
                      color: shiftByType === SKILLS ? "#757575" : textColor,
                      fontSize: "16px",
                      marginTop: "2px",
                    }}
                  />
                }
                fontWeight={400}
                fontSize={12}
                fontColor={textColor}
              />
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {isLayer && (
                  <BoxRow
                    content={`${format(
                      new Date(data.startDateTime),
                      "h:mm a"
                    )} - ${format(new Date(data.endDateTime), "h:mm a")}`}
                    icon={
                      <AccessTime
                        style={{
                          color: shiftByType === SKILLS ? "#757575" : textColor,
                          fontSize: "16px",
                          marginTop: "2px",
                        }}
                      />
                    }
                    fontWeight={400}
                    fontSize={12}
                    fontColor={textColor}
                  />
                )}
                {data.hasWorkersFromOtherLocations && isLayer && (
                  <GroupsOutlinedIcon
                    fontSize="small"
                    style={{
                      whiteSpace: "normal",
                      color: shiftByType === SKILLS ? "#757575" : textColor,
                      fontSize: "16px",
                      marginRight: "6px",
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box>
              {data.hasWorkersFromOtherLocations && !isLayer && (
                <GroupsOutlinedIcon
                  fontSize="small"
                  style={{
                    whiteSpace: "normal",
                    color: shiftByType === SKILLS ? "#757575" : textColor,
                    fontSize: "16px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
